import { type ReactNode, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { AdminShell } from '@/components/shell/AdminShell';
import { handbookUrl } from '@/handbook/handbookUrls';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import { LEGACY_MONEY_PATHS } from '@/lib/orgNav';
import { type AdminAccess, limitedHome } from '@/lib/platformAdmin';
import { IntegrationBuilderListPage } from '@/pages/IntegrationBuilder/IntegrationBuilderListPage';
import { IntegrationBuilderPage } from '@/pages/IntegrationBuilder/IntegrationBuilderPage';
import { FabricsPage } from '@/pages/FabricsPage';
import { OrganizationsPage } from '@/pages/OrganizationsPage';
import { OrgFabricPage } from '@/pages/org/OrgFabricPage';
import { OrgIntegrationSettingsPage } from '@/pages/org/OrgIntegrationSettingsPage';
import { OrgLayout, OrgSectionNotFound } from '@/pages/org/OrgLayout';
import { OrgMembersPage } from '@/pages/org/OrgMembersPage';
import { OrgMoneyPage, OrgMoneyRedirect } from '@/pages/org/OrgMoneyPage';
import { OrgOverviewPage } from '@/pages/org/OrgOverviewPage';
import { OrgProjectsPage } from '@/pages/org/OrgProjectsPage';
import { PlatformPage } from '@/pages/PlatformPage/PlatformPage';
import { ProjectDetailPage } from '@/pages/ProjectDetailPage';
import { RunDetailPage } from '@/pages/RunDetailPage';
import { RunsPage } from '@/pages/Runs/RunsPage';
import { SalesMetricsDashboardPage } from '@/pages/SalesMetricsDashboardPage';
import { SessionDetailPage } from '@/pages/SessionDetailPage';
import { SkillsPage } from '@/pages/Skills/SkillsPage';
import { TemplateDetailPage } from '@/pages/Templates/TemplateDetailPage';
import { TemplatesListPage } from '@/pages/Templates/TemplatesListPage';
import { TokenHealthPage } from '@/pages/TokenHealth/TokenHealthPage';
import { PersonPage } from '@/pages/Users/PersonPage';
import { UserChangesPage } from '@/pages/Users/UserChangesPage';
import { UsersPage } from '@/pages/Users/UsersPage';

/** The Handbook is its own site now; an old `/handbook/...` link here goes to the same page there. */
function HandbookRedirect() {
  const location = useLocation();
  useEffect(() => {
    window.location.replace(`${handbookUrl(location.pathname)}${location.hash}`);
  }, [location.hash, location.pathname]);
  return null;
}

/** What a studio admin sees on an Operations page: those are a platform admin's. */
function OperationsOnly({ children }: { children: ReactNode }) {
  const access = useAdminAccess();
  if (access.isPlatformAdmin) return <>{children}</>;
  return (
    <div className="mx-auto max-w-xl px-6 py-16 text-center">
      <h1 className="text-lg font-semibold text-foreground">Operations is for platform admins</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Runs, sessions, Fabrics, Token health and the Platform page need the Platform Admin role. A platform admin can open this for you.
      </p>
    </div>
  );
}

/** Integration builders and template curators without platform admin get their own pages, nothing else. */
function LimitedRoutes({ access }: { access: AdminAccess }) {
  return (
    <Routes>
      {access.canBuildIntegrations ? <Route path="/integrations/builder" element={<IntegrationBuilderListPage />} /> : null}
      {access.canBuildIntegrations ? <Route path="/integrations/builder/:draftId" element={<IntegrationBuilderPage />} /> : null}
      {access.canCurateTemplates ? <Route path="/templates" element={<TemplatesListPage />} /> : null}
      {access.canCurateTemplates ? <Route path="/templates/:templateId" element={<TemplateDetailPage />} /> : null}
      {access.canCurateSkills ? <Route path="/skills" element={<SkillsPage />} /> : null}
      <Route path="/handbook/*" element={<HandbookRedirect />} />
      <Route path="*" element={<Navigate to={limitedHome(access)} replace />} />
    </Routes>
  );
}

export function AdminApp() {
  const access = useAdminAccess();
  if (!access.isStudioAdmin) {
    return (
      <AdminShell>
        <LimitedRoutes access={access} />
      </AdminShell>
    );
  }
  return (
    <AdminShell>
      <Routes>
        <Route path="/" element={<SalesMetricsDashboardPage />} />
        <Route path="/organizations" element={<OrganizationsPage />} />
        <Route path="/organizations/:orgId" element={<OrgLayout />}>
          <Route index element={<OrgOverviewPage />} />
          <Route path="projects" element={<OrgProjectsPage />} />
          <Route path="projects/:projectId" element={<ProjectDetailPage />} />
          <Route path="sessions/:sessionId" element={<OperationsOnly><SessionDetailPage /></OperationsOnly>} />
          <Route path="runs/:runId" element={<OperationsOnly><RunDetailPage /></OperationsOnly>} />
          <Route path="members" element={<OrgMembersPage />} />
          <Route path="fabric" element={<OperationsOnly><OrgFabricPage /></OperationsOnly>} />
          <Route path="integration-settings" element={<OrgIntegrationSettingsPage />} />
          <Route path="money" element={<OrgMoneyPage />} />
          {Object.entries(LEGACY_MONEY_PATHS).map(([path, section]) => (
            <Route key={path} path={path} element={<OrgMoneyRedirect section={section} />} />
          ))}
          <Route path="*" element={<OrgSectionNotFound />} />
        </Route>
        <Route path="/users" element={<UsersPage />} />
        <Route path="/users/changes" element={<UserChangesPage />} />
        <Route path="/users/:userId" element={<PersonPage />} />
        <Route path="/platform" element={<OperationsOnly><PlatformPage /></OperationsOnly>} />
        <Route path="/runs" element={<OperationsOnly><RunsPage /></OperationsOnly>} />
        <Route path="/fabrics" element={<OperationsOnly><FabricsPage /></OperationsOnly>} />
        <Route path="/token-health" element={<OperationsOnly><TokenHealthPage /></OperationsOnly>} />
        <Route path="/ops" element={<Navigate to="/platform" replace />} />
        <Route path="/ops/microvms" element={<Navigate to="/platform" replace />} />
        <Route path="/integrations/builder" element={<IntegrationBuilderListPage />} />
        <Route path="/integrations/builder/:draftId" element={<IntegrationBuilderPage />} />
        <Route path="/templates" element={<TemplatesListPage />} />
        <Route path="/templates/:templateId" element={<TemplateDetailPage />} />
        <Route path="/skills" element={<SkillsPage />} />
        <Route path="/handbook/*" element={<HandbookRedirect />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AdminShell>
  );
}
